import React, { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  LayoutGrid, BarChart3, Calendar, Plus, Bot, FileText, Search,
  ChevronDown, ChevronUp, CheckCircle, Clock, AlertTriangle, Users, ListTodo,
} from "lucide-react";
import { useTasks } from "@/hooks/useTasks";
import { listMembros } from "@/services/profilesCache";
import { getCurrentUserId } from "@/utils/userUtils";
import type { Task2, Column, Project } from "@/types/tasks";
import { TasksTableView } from "@/components/tasks/TasksTableView";
import { TasksGanttView } from "@/components/tasks/TasksGanttView";
import { KanbanBoard } from "@/components/tasks2/KanbanBoard";
import { TaskDetailsModal } from "@/components/tasks2/TaskDetailsModal";
import { NewTaskModal } from "@/components/dashboard/NewTaskModal";

// Tipos legados ainda consumidos por componentes antigos (TaskDetailsPanel,
// AITaskSuggestions, TasksFilters, TasksKanbanView). Mantidos para compilar.
export type ViewMode = "table" | "kanban" | "gantt";
export interface Task {
  id: string;
  title: string;
  description?: string;
  status: "not_started" | "in_progress" | "waiting" | "completed" | "cancelled";
  priority: "low" | "medium" | "high";
  assignee: { id: string; name: string; avatar?: string };
  obra: string;
  etapa: string;
  setor: string;
  tipo: string;
  startDate?: Date;
  endDate?: Date;
  createdAt: Date;
  updatedAt: Date;
  comments: number;
  attachments: number;
}

// Colunas fixas do kanban, na ordem do fluxo de trabalho.
// "Cancelado" fica fora do board (não é etapa do fluxo), mas existe como filtro
// e como coluna sob demanda — antes a tarefa cancelada sumia de todas as visões
// menos a Tabela, e os totais do Resumo não fechavam com os cards exibidos.
const COLUMN_DEFS: Array<{ id: string; title: string; color: string }> = [
  { id: "not_started", title: "Não iniciado", color: "#6B7280" },
  { id: "in_progress", title: "Em execução", color: "#0071CE" },
  { id: "waiting", title: "Aguardando", color: "#F59E0B" },
  { id: "completed", title: "Concluído", color: "#28A745" },
];

const CANCELLED_COLUMN = { id: "cancelled", title: "Cancelado", color: "#DC3545" };

/** Minúsculas e sem acentos, para busca tolerante à digitação de campo. */
const normalizar = (s: string) =>
  s.trim().toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");

const VIEWS: Array<{ id: ViewMode; label: string; icon: typeof LayoutGrid }> = [
  { id: "table", label: "Tabela", icon: BarChart3 },
  { id: "kanban", label: "Kanban", icon: LayoutGrid },
  { id: "gantt", label: "Cronograma", icon: Calendar },
];

export default function Tasks() {
  const { tasks, loading, createTask, updateTaskStatus, updateTask, refetch } = useTasks();
  const membros = listMembros();
  const meuId = getCurrentUserId();

  // No mobile o Kanban exige muito scroll horizontal (4 colunas); a Tabela é
  // mais usável. Desktop mantém o Kanban como padrão. O usuário pode alternar.
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
      ? "table"
      : "kanban",
  );
  const [showResumo, setShowResumo] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");

  const [selectedTask, setSelectedTask] = useState<Task2 | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showNewTask, setShowNewTask] = useState(false);

  // Projetos (obras) derivados das tarefas reais
  const projects: Project[] = useMemo(
    () =>
      Array.from(
        new Map(
          tasks.map((t) => [t.projectId, { id: t.projectId, name: t.projectName, color: "#0071CE" }]),
        ).values(),
      ),
    [tasks],
  );

  // Filtro aplicado a todas as visões. A busca ignora acentos: quem digita no
  // canteiro raramente acentua, e "vergalhao" precisa achar "vergalhão".
  const filtered = useMemo(() => {
    const term = normalizar(search);
    return tasks.filter((t) => {
      const matchSearch =
        !term ||
        normalizar(t.title).includes(term) ||
        normalizar(t.description ?? "").includes(term) ||
        normalizar(t.setor ?? "").includes(term) ||
        normalizar(t.projectName ?? "").includes(term) ||
        t.assignees.some((a) => normalizar(a.name).includes(term));
      const matchStatus = statusFilter === "all" || t.status === statusFilter;
      const matchProject = projectFilter === "all" || t.projectId === projectFilter;
      const matchAssignee =
        assigneeFilter === "all" ||
        (assigneeFilter === "unassigned"
          ? t.assignees.length === 0
          : t.assignees.some((a) => a.id === assigneeFilter));
      return matchSearch && matchStatus && matchProject && matchAssignee;
    });
  }, [tasks, search, statusFilter, projectFilter, assigneeFilter]);

  // A coluna "Cancelado" só aparece quando há tarefa cancelada no recorte atual,
  // para não poluir o board de quem não usa esse estado.
  const columns: Column[] = useMemo(() => {
    const defs = filtered.some((t) => t.status === CANCELLED_COLUMN.id)
      ? [...COLUMN_DEFS, CANCELLED_COLUMN]
      : COLUMN_DEFS;
    return defs.map((def) => ({ ...def, tasks: filtered.filter((t) => t.status === def.id) }));
  }, [filtered]);

  // Estatísticas reais (para o Resumo recolhível)
  const stats = useMemo(() => {
    const now = new Date();
    const total = filtered.length;
    const concluidas = filtered.filter((t) => t.status === "completed").length;
    const emAndamento = filtered.filter((t) => t.status === "in_progress").length;
    const atrasadas = filtered.filter(
      (t) => t.endDate && t.endDate < now && t.status !== "completed",
    ).length;
    const responsaveis = new Set(
      filtered.flatMap((t) => t.assignees.map((a) => a.id)).filter(Boolean),
    ).size;

    const porObra = projects
      .map((p) => {
        const doProjeto = filtered.filter((t) => t.projectId === p.id);
        const done = doProjeto.filter((t) => t.status === "completed").length;
        return {
          obra: p.name,
          total: doProjeto.length,
          completed: done,
          progress: doProjeto.length ? Math.round((done / doProjeto.length) * 100) : 0,
        };
      })
      .filter((x) => x.total > 0);

    return { total, concluidas, emAndamento, atrasadas, responsaveis, porObra };
  }, [filtered, projects]);

  const openDetails = (task: Task2) => {
    setSelectedTask(task);
    setShowDetails(true);
  };

  const handleCreateFromColumn = async (data: Partial<Task2>) => {
    if (!data.title) return;
    await createTask({
      title: data.title,
      priority: data.priority ?? "medium",
      endDate: data.endDate,
      workId: projectFilter !== "all" ? projectFilter : data.workId,
      projectId: data.projectId,
      setor: data.setor,
      status: data.status, // cria já na coluna escolhida (não força "not_started")
    });
    refetch();
  };

  const renderView = () => {
    if (loading && tasks.length === 0) {
      return (
        <div className="space-y-2">
          <div className="h-12 rounded-md bg-muted animate-pulse" />
          <div className="h-12 rounded-md bg-muted animate-pulse" />
          <div className="h-12 w-2/3 rounded-md bg-muted animate-pulse" />
        </div>
      );
    }
    switch (viewMode) {
      case "table":
        return <TasksTableView tasks={filtered} onTaskSelect={openDetails} />;
      case "gantt":
        return <TasksGanttView tasks={filtered} onTaskSelect={openDetails} />;
      default:
        return (
          <KanbanBoard
            columns={columns}
            onTaskMove={(taskId, newStatus) => updateTaskStatus(taskId, newStatus)}
            onTaskClick={openDetails}
            onColumnUpdate={() => { /* colunas derivadas das tarefas; ordem não persistida */ }}
            onCreateTask={handleCreateFromColumn}
            onOpenTaskModal={() => setShowNewTask(true)}
          />
        );
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-background">
      <div className="p-6 space-y-4">
        {/* Cabeçalho: título + seletor de visão + ações */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Gestor de tarefas</h1>
            <p className="text-muted-foreground">Organize e acompanhe suas obras por etapas e status</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex bg-muted rounded-lg p-1">
              {VIEWS.map((v) => {
                const Icon = v.icon;
                return (
                  <Button
                    key={v.id}
                    variant={viewMode === v.id ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode(v.id)}
                    aria-label={v.label}
                    className="gap-2 h-11 sm:h-9"
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{v.label}</span>
                  </Button>
                );
              })}
            </div>

            <Button variant="outline" size="sm" className="gap-2 h-11 sm:h-9" onClick={() => setShowResumo((s) => !s)}>
              {showResumo ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Resumo
            </Button>
            <Button variant="outline" size="sm" className="gap-2 h-11 sm:h-9" aria-label="Sugestões IA" disabled>
              <Bot className="w-4 h-4" />
              <span className="hidden sm:inline">Sugestões IA</span>
            </Button>
            <Button size="sm" className="gap-2 h-11 sm:h-9" data-testid="nova-tarefa" onClick={() => setShowNewTask(true)}>
              <Plus className="w-4 h-4" />
              Nova tarefa
            </Button>
          </div>
        </div>

        {/* Resumo recolhível — recolhido por padrão para as tarefas virem primeiro */}
        {showResumo ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {[
                { title: "Total de tarefas", value: stats.total, icon: BarChart3, color: "text-blue-600" },
                { title: "Concluídas", value: stats.concluidas, icon: CheckCircle, color: "text-green-600" },
                { title: "Em andamento", value: stats.emAndamento, icon: Clock, color: "text-orange-600" },
                { title: "Atrasadas", value: stats.atrasadas, icon: AlertTriangle, color: "text-red-600" },
                { title: "Responsáveis", value: stats.responsaveis, icon: Users, color: "text-purple-600" },
              ].map((s) => {
                const Icon = s.icon;
                return (
                  <Card key={s.title}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-muted-foreground">{s.title}</p>
                          <p className="text-xl font-bold text-foreground">{s.value}</p>
                        </div>
                        <Icon className={`w-6 h-6 ${s.color}`} />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {stats.porObra.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold mb-3">Progresso por obra</h3>
                  <div className="space-y-3">
                    {stats.porObra.map((item) => (
                      <div key={item.obra} className="space-y-1">
                        <div className="flex justify-between items-center text-sm">
                          <span className="font-medium">{item.obra}</span>
                          <span className="text-muted-foreground">{item.completed}/{item.total} tarefas</span>
                        </div>
                        <Progress value={item.progress} className="h-2" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <button
            onClick={() => setShowResumo(true)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-border text-xs text-muted-foreground hover:bg-muted/40 transition-colors"
          >
            <ChevronDown className="w-4 h-4" />
            Resumo — {stats.total} tarefas · {stats.concluidas} concluídas · {stats.atrasadas} atrasadas
          </button>
        )}

        {/* Filtros — valem para as três visões */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar tarefa"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-11 sm:h-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-11 sm:h-9 w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              {[...COLUMN_DEFS, CANCELLED_COLUMN].map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="h-11 sm:h-9 w-[170px]"><SelectValue placeholder="Obra" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as obras</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className="h-11 sm:h-9 w-[200px]"><SelectValue placeholder="Responsável" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os responsáveis</SelectItem>
              {meuId && <SelectItem value={meuId}>Minhas tarefas</SelectItem>}
              <SelectItem value="unassigned">Sem responsável</SelectItem>
              {membros.map((m) => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Visão selecionada */}
        {renderView()}
      </div>

      {/* Nova tarefa (persiste via useTasks). Renderizado condicionalmente para
          desmontar de fato ao fechar — evita o modal preso pela animação de saída. */}
      {showNewTask && (
        <NewTaskModal
          open={showNewTask}
          onOpenChange={setShowNewTask}
          onTaskCreated={() => refetch()}
          defaultWorkId={projectFilter !== "all" ? projectFilter : undefined}
        />
      )}

      {/* Detalhes da tarefa */}
      {showDetails && selectedTask && (
        <TaskDetailsModal
          task={selectedTask}
          projects={projects}
          isOpen={showDetails}
          onClose={() => { setShowDetails(false); setIsFullscreen(false); }}
          onUpdate={(updated) => {
            // Persiste título, descrição, prioridade, status e prazo.
            updateTask(updated.id, updated);
          }}
          isFullscreen={isFullscreen}
          onToggleFullscreen={() => setIsFullscreen((f) => !f)}
        />
      )}
    </div>
  );
}
